import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, User, Calendar, Gamepad2, BarChart3 } from 'lucide-react';
import { teamsAPI, playersAPI, tournamentsAPI, matchesAPI } from '../services/api';

const Home = () => {
  const [stats, setStats] = useState({
    activeTournaments: 0,
    teams: 0,
    players: 0,
    matchesPlayed: 0,
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [tournaments, teams, players, matches] = await Promise.all([
          tournamentsAPI.getTournaments({ status: 'ongoing' }).catch(() => []),
          teamsAPI.getTeams().catch(() => []),
          playersAPI.getPlayers().catch(() => []),
          matchesAPI.getMatches({ status: 'completed' }).catch(() => [])
        ]);

        setStats({
          activeTournaments: Array.isArray(tournaments) ? tournaments.length : 0,
          teams: Array.isArray(teams) ? teams.length : 0,
          players: Array.isArray(players) ? players.length : 0,
          matchesPlayed: Array.isArray(matches) ? matches.length : 0,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);
  const features = [
    {
      title: 'Team Management',
      description: 'Create and manage cricket teams with player rosters',
      icon: Users,
      link: '/dashboard/teams',
      color: 'bg-blue-500'
    },
    {
      title: 'Player Profiles',
      description: 'Track player statistics and performance metrics',
      icon: User,
      link: '/dashboard/players',
      color: 'bg-green-500'
    },
    {
      title: 'Tournaments',
      description: 'Organize and manage cricket tournaments',
      icon: Calendar,
      link: '/dashboard/tournaments',
      color: 'bg-purple-500'
    },
    {
      title: 'Match Management',
      description: 'Schedule and track matches between teams',
      icon: Gamepad2,
      link: '/dashboard/matches',
      color: 'bg-orange-500'
    },
    {
      title: 'Live Scorecards',
      description: 'Real-time scoring and match statistics',
      icon: BarChart3,
      link: '/dashboard/scorecards',
      color: 'bg-red-500'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center py-8 md:py-12 px-4 bg-gradient-to-r from-cricket-green to-green-600 rounded-lg text-white">
        <img src="/logo.png" alt="Innings Pro" className="h-24 w-48 mx-auto mb-4" />
        <h1 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">Innings Pro</h1>
        <p className="text-base md:text-xl mb-6 md:mb-8 px-2">Comprehensive platform for managing cricket tournaments, teams, players, and live scoring</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:space-x-4">
          <Link
            to="/dashboard/tournaments"
            className="bg-white text-cricket-green px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Create Tournament
          </Link>
          <Link
            to="/dashboard/teams"
            className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-cricket-green transition-colors"
          >
            Manage Teams
          </Link>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <Link
            key={index}
            to={feature.link}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200 hover:border-cricket-green"
          >
            <div className={`${feature.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
              <feature.icon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </Link>
        ))}
      </div>

      {/* Stats Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Stats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-cricket-green">
              {stats.loading ? '-' : stats.activeTournaments}
            </div>
            <div className="text-gray-600">Active Tournaments</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-500">
              {stats.loading ? '-' : stats.teams}
            </div>
            <div className="text-gray-600">Teams</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-500">
              {stats.loading ? '-' : stats.players}
            </div>
            <div className="text-gray-600">Players</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500">
              {stats.loading ? '-' : stats.matchesPlayed}
            </div>
            <div className="text-gray-600">Matches Played</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
